import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { apiFetch } from "../api/client";
import {
    HomeIcon,
    CubeIcon,
    ShoppingBagIcon,
    CurrencyDollarIcon,
    ArrowPathIcon,
    BuildingStorefrontIcon,
    ChevronDownIcon,
    SparklesIcon,
} from "@heroicons/react/24/outline";
import { hasRole, Roles } from "../auth/authorization";

function SideMenu({ onOpenSubscriptionModal = () => {}, className, isMobile = false, onNavigate = () => {}, subscriptionPlan = null, planLoaded = false }) {
    const location = useLocation();
    // detect tenant prefix like /t/:slug or tenant as first segment like /{slug}
    const segments = (location.pathname || "").replace(/^\/+|\/+$/g, "").split("/");
    let base = "";
    const nonTenantPrefixes = ["api", "login", "register", "forgot-password", "verify-email", "reset-password", "static", "assets", "favicon.ico"];
    const storedTenant = localStorage.getItem("tenant");
    if (storedTenant) {
        base = `/${storedTenant}`;
    } else if (segments.length >= 2 && segments[0].toLowerCase() === "t") {
        base = `/t/${segments[1]}`;
    } else if (segments.length >= 1 && segments[0] && !nonTenantPrefixes.includes(segments[0].toLowerCase())) {
        base = `/${segments[0]}`;
    }
    const localStorageData = JSON.parse(localStorage.getItem("user") || "{}") || {};

    // Use subscription plan passed from parent (loaded globally in App.js)
    const planName = subscriptionPlan?.name || null;
    const planExpiry = subscriptionPlan?.expiresAt || null;
    const subscriptionDaysLeft = planExpiry ? Math.ceil((new Date(planExpiry) - new Date()) / 86400000) : null;
    const isBasic = Boolean(planName && String(planName).toLowerCase() === 'basic');
    const isPremium = Boolean(planName && String(planName).toLowerCase().includes('premium') && (!planExpiry || new Date(planExpiry) > new Date()));
    const [openSections, setOpenSections] = useState({});

    // Check if user is authorized to see Manage Users - only show after plan data is loaded to avoid flicker
    const canManageUsers = planLoaded && hasRole(Roles.StokrazeUser) && !isBasic && !hasRole(Roles.SalesRep);
    const canManageSubscriptions = hasRole(Roles.StokrazeUser);
    
    const toggleSection = (key) => {
        setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    return (
        <div className={className || "h-full flex-col justify-between bg-white hidden lg:flex"}>
            <div className="px-4 py-6">
                <nav aria-label="Main Nav" className="mt-6 flex flex-col space-y-1">
                    {/* Dashboard */}
                    <Link
                        to="."
                        onClick={() => { if (isMobile) onNavigate(); }}
                        className="flex items-center gap-2 rounded-lg hover:bg-gray-100 px-4 py-2 text-gray-700"
                    >
                        <HomeIcon className="h-5 w-5" />
                        <span className="text-sm font-medium"> Dashboard </span>
                    </Link>

                    {/* Inventory */}
                    {isMobile ? (
                        <div className="border-b">
                            <div className="flex items-center justify-between px-4 py-2">
                                <div className="flex items-center gap-2 text-gray-700">
                                    <CubeIcon className="h-5 w-5" />
                                    <span className="text-sm font-medium"> Inventory </span>
                                </div>
                                <button onClick={() => toggleSection('inventory')} aria-expanded={!!openSections.inventory} className="p-1">
                                    <ChevronDownIcon className={`h-5 w-5 text-gray-500 transform transition-transform ${openSections.inventory ? 'rotate-180' : 'rotate-0'}`} />
                                </button>
                            </div>
                                    {openSections.inventory && (
                                <div className="pl-8 pb-2">
                                    <Link to="inventory" onClick={() => { if (isMobile) onNavigate(); }} className="block px-4 py-1 text-gray-700">Open Inventory</Link>
                                </div>
                            )}
                        </div>
                    ) : (
                        <details className="group [&_summary::-webkit-details-marker]:hidden">
                            <summary className="flex cursor-pointer items-center justify-between rounded-lg px-4 py-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700">
                                <Link to="inventory">
                                    <div className="flex items-center gap-2">
                                        <CubeIcon className="h-5 w-5" />
                                        <span className="text-sm font-medium"> Inventory </span>
                                    </div>
                                </Link>
                            </summary>
                        </details>
                    )}

                    {/* Purchase Details */}
                    {/* <Link
            to="/purchase-details"
            className="flex items-center gap-2 rounded-lg px-4 py-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          >
            <ShoppingBagIcon className="h-5 w-5" />
            <span className="text-sm font-medium"> Purchase Details </span>
          </Link> */}

                    {/* Sales */}
                    <Link
                        to={`${base}/sales`}
                        onClick={() => { if (isMobile) onNavigate(); }}
                        className="flex items-center gap-2 rounded-lg px-4 py-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                    >
                        <CurrencyDollarIcon className="h-5 w-5" />
                        <span className="text-sm font-medium"> Sales </span>
                    </Link>

                    {/* Manage Subscription - navigate to subscription page (no modal) */}
                    {canManageSubscriptions && (
                    <Link
                        to={`${base}/subscription`}
                        onClick={() => { if (isMobile) onNavigate(); }}
                        className="w-full text-left flex items-center gap-2 rounded-lg px-4 py-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                    >
                        <CurrencyDollarIcon className="h-5 w-5" />
                        <span className="text-sm font-medium"> Manage Subscription </span>
                        {planLoaded && (
                            <span className="ml-auto inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                                {planName || 'Free'}
                            </span>
                        )}
                    </Link>
                    )}

                    {isPremium && (
                        <Link
                            to={`${base}/ai-performance`}
                            onClick={() => { if (isMobile) onNavigate(); }}
                            className="flex items-center gap-2 rounded-lg px-4 py-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                        >
                            <SparklesIcon className="h-5 w-5" />
                            <span className="text-sm font-medium"> AI Performance </span>
                            <span className="ml-auto rounded-full bg-[#D326AD]/20 px-2 py-0.5 text-xs font-semibold text-[#f3a1dc]">Premium</span>
                        </Link>
                    )}

                    {/* Manage Vendor */}
                    {isMobile ? (
                        <div className="border-b">
                            <div className="flex items-center justify-between px-4 py-2">
                                <div className="flex items-center gap-2 text-gray-700">
                                    <BuildingStorefrontIcon className="h-5 w-5" />
                                    <span className="text-sm font-medium"> Manage Vendor </span>
                                </div>
                                <button onClick={() => toggleSection('vendor')} aria-expanded={!!openSections.vendor} className="p-1">
                                    <ChevronDownIcon className={`h-5 w-5 text-gray-500 transform transition-transform ${openSections.vendor ? 'rotate-180' : 'rotate-0'}`} />
                                </button>
                            </div>
                                    {openSections.vendor && (
                                <div className="pl-8 pb-2">
                                    <Link to={`${base}/manage-store`} onClick={() => { if (isMobile) onNavigate(); }} className="block px-4 py-1 text-gray-700">Open Manage Vendor</Link>
                                </div>
                            )}
                        </div>
                    ) : (
                        <details className="group [&_summary::-webkit-details-marker]:hidden">
                            <summary className="flex cursor-pointer items-center justify-between rounded-lg px-4 py-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700">
                                <Link to={`${base}/manage-store`}>
                                    <div className="flex items-center gap-2">
                                        <BuildingStorefrontIcon className="h-5 w-5" />
                                        <span className="text-sm font-medium"> Manage Vendor </span>
                                    </div>
                                </Link>
                            </summary>
                        </details>
                    )}

                    {/* Manage Category */}
                    {isMobile ? (
                        <div className="border-b">
                            <div className="flex items-center justify-between px-4 py-2">
                                <div className="flex items-center gap-2 text-gray-700">
                                    <ShoppingBagIcon className="h-5 w-5" />
                                    <span className="text-sm font-medium"> Manage Category </span>
                                </div>
                                <button onClick={() => toggleSection('category')} aria-expanded={!!openSections.category} className="p-1">
                                    <ChevronDownIcon className={`h-5 w-5 text-gray-500 transform transition-transform ${openSections.category ? 'rotate-180' : 'rotate-0'}`} />
                                </button>
                            </div>
                                    {openSections.category && (
                                <div className="pl-8 pb-2">
                                    <Link to={`${base}/manage-category`} onClick={() => { if (isMobile) onNavigate(); }} className="block px-4 py-1 text-gray-700">Open Manage Category</Link>
                                </div>
                            )}
                        </div>
                    ) : (
                        <details className="group [&_summary::-webkit-details-marker]:hidden">
                            <summary className="flex cursor-pointer items-center justify-between rounded-lg px-4 py-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700">
                                <Link to={`${base}/manage-category`}>
                                    <div className="flex items-center gap-2">
                                        <ShoppingBagIcon className="h-5 w-5" />
                                        <span className="text-sm font-medium"> Manage Category </span>
                                    </div>
                                </Link>
                            </summary>
                        </details>
                    )}

                    {/* Manage Users - only visible to StokrazeUser role (not SalesRep, and only after plan data loads) */}
                    {canManageUsers && (
                        <Link
                            to={`${base}/manage-users`}
                            onClick={() => { if (isMobile) onNavigate(); }}
                            className="flex items-center gap-2 rounded-lg px-4 py-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                        >
                            <div className="flex items-center gap-2">
                                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M16 11c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM8 11c1.657 0 3-1.343 3-3S9.657 5 8 5 5 6.343 5 8s1.343 3 3 3zM8 13c-2.33 0-7 1.17-7 3.5V19h14v-2.5C15 14.17 10.33 13 8 13zM16 13c-.29 0-.62.02-.97.05 1.16.84 1.97 2.06 1.97 3.45V19h6v-2.5C23 14.17 18.33 13 16 13z" /></svg>
                                <span className="text-sm font-medium"> Manage Users </span>
                            </div>
                        </Link>
                    )}

                    {/* Returned Product */}
                    {isMobile ? (
                        <div className="border-b">
                            <div className="flex items-center justify-between px-4 py-2">
                                <div className="flex items-center gap-2 text-gray-700">
                                    <ArrowPathIcon className="h-5 w-5" />
                                    <span className="text-sm font-medium"> Returned Product </span>
                                </div>
                                <button onClick={() => toggleSection('returned')} aria-expanded={!!openSections.returned} className="p-1">
                                    <ChevronDownIcon className={`h-5 w-5 text-gray-500 transform transition-transform ${openSections.returned ? 'rotate-180' : 'rotate-0'}`} />
                                </button>
                            </div>
                            {openSections.returned && (
                                <div className="pl-8 pb-2">
                                    <Link to="returned-products" onClick={() => { if (isMobile) onNavigate(); }} className="block px-4 py-1 text-gray-700">Open Returned Products</Link>
                                </div>
                            )}
                        </div>
                    ) : (
                        <details className="group [&_summary::-webkit-details-marker]:hidden">
                            <summary className="flex cursor-pointer items-center justify-between rounded-lg px-4 py-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700">
                                <Link to="returned-products">
                                    <div className="flex items-center gap-2">
                                        <ArrowPathIcon className="h-5 w-5" />
                                        <span className="text-sm font-medium"> Returned Product </span>
                                    </div>
                                </Link>
                            </summary>
                        </details>
                    )}
                </nav>
            </div>

            {/* Profile Section removed */}
        </div>
    );
}

export default SideMenu;

// simple subscription modal markup appended to file so we don't add new files
// SubscriptionModal removed — navigation goes to subscription page instead
